import { useState } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

import { TextField } from 'components/styledFormElements/TextField'
import { ToggleField } from 'components/styledFormElements/ToggleField'
import { Button } from 'components/ui/button'
import { Loader } from 'components/loader'
import { DialogWrapper } from 'components/dialogWrapper'

import { useNotificationBar } from 'context/notificationContext'
import { getErrorResponse } from 'services/util'

import { useAddIcon } from '../../../../../src/hooks/useIconMaster'
import { X, Upload } from 'lucide-react'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { useObjectUrl } from 'hooks/useObjectUrl'

interface Props {
  isOpen: boolean
  setIsOpen: (v: boolean) => void
  onSubmitHandler?: () => void
}

/* ---------------- VALIDATION ---------------- */

const schema = yup.object({
  icon_name: yup.string().required('Icon name is required'),
  status: yup.boolean(),
})

const AddIconPopup = ({
  isOpen,
  setIsOpen,
  onSubmitHandler,
}: Props) => {
  const notification = useNotificationBar()
  const [iconFile, setIconFile] = useState<File | null>(null)
  const iconPreviewUrl = useObjectUrl(iconFile)
  const [iconError, setIconError] = useState('')

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onTouched',
    defaultValues: {
      status: true,
    },
  })

  const { mutate: addIcon, isLoading } = useAddIcon()

  /* ---------------- SUBMIT ---------------- */

  const onSubmit = (values: any) => {
    if (!iconFile) {
      setIconError('Icon image is required')
      return
    }

    setIconError('')

    const formData = new FormData()
    formData.append('name', values.icon_name)
    formData.append('status', values.status ? '1' : '0')
    formData.append('icon', iconFile)

    addIcon(formData, {
      onSuccess: () => {
        notification.push({
          title: 'Success',
          message: 'Icon added successfully',
          type: 'success',
        })
        onSubmitHandler?.()
        handleClose()
      },
      onError: (err) =>
        notification.push(getErrorResponse(err)),
    })
  }

  /* ---------------- CLOSE ---------------- */

  const handleClose = () => {
    reset()
    setIconFile(null)
    setIconError('')
    setIsOpen(false)
  }

  /* ---------------- RENDER ---------------- */

  return (
    <DialogWrapper
      title="Add Icon"
      isOpen={isOpen}
      setIsOpen={setIsOpen}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="grid grid-cols-1 md:grid-cols-1 gap-4"
      >
        <TextField
          register={register('icon_name')}
          errors={errors}
          label="Icon Name"
        />



        <div className="col-span-full">

          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Upload Icon <span className="text-red-500">*</span>
            {/* Tooltip */}
            <div className="relative group mr-auto ml-1 mt-0 inline-block align-[sub]">
              <InformationCircleIcon className="w-4 h-4 text-slate-500 cursor-pointer" />

              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 text-xs text-slate-700 dark:text-slate-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50
 ">
                Recommended Size - Under 5 MB. Supports SVG.
              </div>
            </div>

          </label>

          <div className="border-2 border-dashed rounded-lg p-4 hover:border-blue-400 transition">
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setIconFile(e.target.files?.[0] || null)}
              className="hidden"
              id="iconUpload"
            />

            <label
              htmlFor="iconUpload"
              className="flex flex-col items-center justify-center cursor-pointer"
            >
              {iconFile ? (
                <div className="relative inline-block">
                  <img
                    src={iconPreviewUrl || undefined}
                    alt="Icon Preview"
                    className="w-20 h-20 object-contain rounded-md border"
                  />

                  {/* ❌ Remove Icon */}
                  <button
                    type="button"
                    onClick={() => setIconFile(null)}
                    className="
              absolute -top-2 -right-2
              bg-black/70 hover:bg-black
              text-white
              rounded-full
              w-5 h-5
              flex items-center justify-center
              transition
              z-10
            "
                    title="Remove icon"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <>
                  <Upload
                    className="text-gray-400 mb-2"
                    size={28}
                  />

                  <p className="text-sm text-gray-500">
                    Click to upload icon
                  </p>

                </>
              )}
            </label>
          </div>

          {iconError && (
            <p className="text-sm text-red-500 mt-1">
              {iconError}
            </p>
          )}
        </div>


        {/* <ToggleField
          register={register('status')}
          label="Status"
          display={watch('status') ? 'Active' : 'Inactive'}
        /> */}

        <div className="col-span-full flex justify-end gap-2">
          <Button
            type="button"
            color="basic"
            onClick={handleClose}
          >
            Cancel
          </Button>

          <Button
            type="submit"
            color="blue"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader /> Saving…
              </>
            ) : (
              'Save'
            )}
          </Button>
        </div>
      </form>
    </DialogWrapper>
  )
}

export default AddIconPopup
